import { queriesEnabledAtom } from "@/state/app.state";
import { syncAttemptCompletedAtom } from "@/state/sync.state";
import {
  useQuery,
  type QueryKey,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { useAtomValue } from "jotai";

type SyncGatedQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> = Omit<
  UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  "enabled"
> & {
  enabled?: boolean;
};

/** Activity queries wait for a sync attempt to finish before showing data. */
export function useSyncGatedQuery<
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: SyncGatedQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
) {
  const queriesEnabled = useAtomValue(queriesEnabledAtom);
  const syncAttemptCompleted = useAtomValue(syncAttemptCompletedAtom);

  const result = useQuery({
    ...options,
    enabled: queriesEnabled && (options.enabled ?? true),
  });

  return {
    ...result,
    data: syncAttemptCompleted ? result.data : undefined,
    isFetching: !syncAttemptCompleted || result.isFetching,
  };
}
